<p-toast></p-toast>
<hr class="mt-0" />

<form [formGroup]="form" (ngSubmit)="onSave()" class="w-full">
  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardCurrency">
      {{ 'layout.configurations.guardConfiguration.fields.currency' | translate }}
    </label>
    <p-dropdown
      inputId="guardCurrency"
      formControlName="currencyId"
      [options]="activeCurrencies"
      optionLabel="currencyName"
      optionValue="id"
      [filter]="true"
      [appendTo]="'body'"
      [placeholder]="'layout.configurations.guardConfiguration.placeholders.currency' | translate"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('currencyId') }">
    </p-dropdown>
    <div *ngIf="hasControlError('currencyId', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.currencyRequired' | translate }}
    </div>
  </div>

  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardStartDate">
      {{ 'layout.configurations.guardConfiguration.fields.startDate' | translate }}
    </label>
    <p-calendar
      inputId="guardStartDate"
      formControlName="startDate"
      dateFormat="yy-mm-dd"
      [readonlyInput]="true"
      [showIcon]="true"
      [appendTo]="'body'"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('startDate') }">
    </p-calendar>
    <div *ngIf="hasControlError('startDate', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.startDateRequired' | translate }}
    </div>
  </div>

  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardEndDate">
      {{ 'layout.configurations.guardConfiguration.fields.endDate' | translate }}
    </label>
    <p-calendar
      inputId="guardEndDate"
      formControlName="endDate"
      dateFormat="yy-mm-dd"
      [readonlyInput]="true"
      [showIcon]="true"
      [appendTo]="'body'"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('endDate') || hasDateRangeError() || hasPastEndDateCreateError() }">
    </p-calendar>
    <div *ngIf="hasControlError('endDate', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.endDateRequired' | translate }}
    </div>
    <div *ngIf="hasDateRangeError()" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.dateRange' | translate }}
    </div>
    <div *ngIf="hasPastEndDateCreateError()" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.pastEndDateCreate' | translate }}
    </div>
  </div>

  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardLaborable">
      {{ 'layout.configurations.guardConfiguration.fields.laborable' | translate }}
    </label>
    <p-inputNumber
      inputId="guardLaborable"
      formControlName="laborable"
      mode="decimal"
      [min]="0"
      [minFractionDigits]="0"
      [maxFractionDigits]="2"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('laborable') || hasAllCostsZeroError() }">
    </p-inputNumber>
    <div *ngIf="hasControlError('laborable', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.laborableRequired' | translate }}
    </div>
    <div *ngIf="hasAllCostsZeroError()" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.allCostsZero' | translate }}
    </div>
  </div>

  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardNonLaborable">
      {{ 'layout.configurations.guardConfiguration.fields.nonLaborable' | translate }}
    </label>
    <p-inputNumber
      inputId="guardNonLaborable"
      formControlName="nonLaborable"
      mode="decimal"
      [min]="0"
      [minFractionDigits]="0"
      [maxFractionDigits]="2"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('nonLaborable') || hasAllCostsZeroError() }">
    </p-inputNumber>
    <div *ngIf="hasControlError('nonLaborable', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.nonLaborableRequired' | translate }}
    </div>
    <div *ngIf="hasAllCostsZeroError()" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.allCostsZero' | translate }}
    </div>
  </div>

  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardEspecial">
      {{ 'layout.configurations.guardConfiguration.fields.especial' | translate }}
    </label>
    <p-inputNumber
      inputId="guardEspecial"
      formControlName="especial"
      mode="decimal"
      [min]="0"
      [minFractionDigits]="0"
      [maxFractionDigits]="2"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('especial') || hasAllCostsZeroError() }">
    </p-inputNumber>
    <div *ngIf="hasControlError('especial', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.especialRequired' | translate }}
    </div>
    <div *ngIf="hasAllCostsZeroError()" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.allCostsZero' | translate }}
    </div>
  </div>

  <div class="flex flex-column gap-2 mb-4">
    <label emRequiredMark for="guardStatus">
      {{ 'layout.configurations.guardConfiguration.fields.status' | translate }}
    </label>
    <p-dropdown
      inputId="guardStatus"
      formControlName="status"
      [options]="statusOptions"
      optionLabel="label"
      optionValue="value"
      [appendTo]="'body'"
      styleClass="w-full"
      [ngClass]="{ 'p-invalid': isInvalid('status') }">
    </p-dropdown>
    <div *ngIf="hasControlError('status', 'required')" class="text-red-600">
      {{ 'layout.configurations.guardConfiguration.validation.statusRequired' | translate }}
    </div>
  </div>

  <hr />

  <div class="flex justify-content-end gap-2 mb-4">
    <p-button
      type="button"
      severity="secondary"
      [outlined]="true"
      [label]="'layout.configurations.guardConfiguration.actions.cancel' | translate"
      (onClick)="onCancel()">
    </p-button>
    <p-button
      type="submit"
      icon="pi pi-check"
      [loading]="isSaving"
      [label]="'layout.configurations.guardConfiguration.actions.save' | translate">
    </p-button>
  </div>
</form>
